<template>
  <div class="user-resource-form-ui__email">
    <VFormFeedback :class="formFeedbackVariationClassName" :error="errors.email && errors.email[0]">
      <VInput
        v-model="payload.email"
        :config="{ label: 'Email address', required: true }"
        class="slim"
      />
    </VFormFeedback>
  </div>
  <div class="user-resource-form-ui__name">
    <VFormFeedback :class="formFeedbackVariationClassName" :error="errors.name && errors.name[0]">
      <VInput v-model="payload.name" :config="{ label: 'Name', required: true }" class="slim" />
    </VFormFeedback>
  </div>
  <div class="user-resource-form-ui__patches">
    <VFormFeedback
      :class="formFeedbackVariationClassName"
      :error="errors.patches && errors.patches[0]"
    >
      <PatchInput
        v-model="payload.patches"
        :data-client="patchDataClient"
        variant-class-name="slim panel-border-emphasis"
      />
    </VFormFeedback>
  </div>
  <ul class="user-resource-form-ui__roles">
    <li v-for="role in ROLE_OPTIONS" :key="role">
      <VRadio v-model="payload.role" :value="role" :label="role" :disabled="isRoleDisabled(role)" />
    </li>
  </ul>
  <div class="user-resource-form-ui__submit">
    <VBtn class="full-width slim" type="button" :loading="isLoading" @click="onSubmit">{{
      btnText
    }}</VBtn>
  </div>
</template>

<script setup lang="ts">
  // Types
  import type { PropType } from 'vue';
  import type { IUserResourceModel, TErrors, TNewUser, TUser } from '~/misc/user-resource/types';
  import type { TPatchDataClient } from '~/misc/patch/types';
  import { EUserRole } from '~/misc/user/types';

  // Config
  import { DEFAULT_ROLE, ROLE_OPTIONS } from '~/misc/user-resource/manage/config';

  // Composables
  import { useUpsertResource } from '~/misc/resource/composables/use-upsert-resource';

  // Components
  import VFormFeedback from '~/misc/v-form-feedback';
  import VInput from '~/misc/v-input';
  import VBtn from '~/misc/v-btn';
  import VRadio from '~/misc/v-radio';
  import PatchInput from '~/misc/patch/input/PatchInput.vue';

  const props = defineProps({
    model: {
      type: Object as PropType<IUserResourceModel>,
      required: true,
    },
    patchDataClient: {
      type: Object as PropType<TPatchDataClient>,
      required: true,
    },
    user: {
      type: Object as PropType<TNewUser | null>,
      default: null,
    },
    formFeedbackVariationClassName: {
      type: String,
      default: '',
    },
    btnText: {
      type: String,
      default: 'Add',
    },
    fullControl: {
      type: Boolean,
      default: false,
    },
  });

  const { payload, errors, isLoading, submit } = useUpsertResource<TNewUser, TErrors, TUser>(
    props.model,
    {
      email: '',
      name: '',
      role: DEFAULT_ROLE,
      patches: [],
    },
    props.user
  );

  const emit = defineEmits<{
    (e: 'upsert', user: TUser): void;
    (e: 'cancel'): void;
  }>();

  const onSubmit = () => {
    submit()
      .then((user: TUser) => {
        emit('upsert', user);
      })
      .catch(() => {
        // suppress
      });
  };

  const isRoleDisabled = (role: EUserRole): boolean =>
    !props.fullControl && (role === EUserRole.OWNER || props.user?.role === EUserRole.OWNER);
</script>
